import React, { useContext, useEffect } from 'react';
import type { FC } from 'react';
import { useCoordination } from '@atlassiansox/engagekit-ts';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { fg } from '@confluence/feature-gating';
import { useCoordinationClient } from '@confluence/engagement-provider';
import { ErrorDisplay } from '@confluence/error-boundary';
import { AdminKeyEligibilityQuery } from '@confluence/super-admin';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import { PremiumTrialDigestDataModal } from '@confluence/premium-trial';

import { PremiumAdminOnboarding } from './PremiumAdminOnboarding';
import { PremiumUserOnboarding } from './PremiumUserOnboarding';
import { PremiumTrialOnboardingModal, TriggerFrom } from './PremiumTrialOnboardingModal';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from './useIsPremiumOnboardingFeatureDiscoveryEnabled';
import {
	getIsPremiumOnboardingHappened,
	setIsPremiumOnboardingHappened,
} from './isOnboardingHappenedUtils';
import { useShowMidTrialPremiumOnboardingModal } from './useShowMidTrialPremiumOnboardingModal';
import { PremiumTrialOnboardingReverseTrialModal } from './PremiumTrialOnboardingReverseTrialModal';
import { usePTOModalVisibilityOnReverseTrial } from './usePTOModalVisibilityOnReverseTrial';
import { getIsTrialInLast15Days } from './getIsTrialInLast15Days';

const PREMIUM_ONBOARDING_MESSAGE_ID = 'confluence-premium-onboarding';
/**
 * this is an old EP message that we use to save dev time for the PTO2 experiment.
 * It will be replaced with a new message Id when we productionize the project
 */
const MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID = 'upflow-confluence-persistent-upgrade';

type CoordinationWrapperProps = {
	messageId: string;
	children: React.FC<{ stop: () => void }>;
};

const CoordinationWrapper = ({ messageId, children }: CoordinationWrapperProps) => {
	const coordinationClient = useCoordinationClient();
	const [started, stop] = useCoordination(coordinationClient, messageId);

	// this is a "clean up" useEffect such that whenever "started=true" is cleaned up,
	// stop() is called on the coordination client to allow other EP messages
	useEffect(
		() => () => {
			if (started) {
				void stop();
			}
		},
		[started, stop],
	);

	if (!started) return null;

	return children({ stop });
};

export const PremiumOnboarding: FC<{}> = () => {
	const { edition } = useSessionData();
	const { isNewUser, isSiteAdmin, error, loading } = useContext(SPAViewContext);
	const {
		isPremiumOnboardingFeatureDiscoveryEnabled,
		isPTORTEnabled,
		inTrialError: experimentInTrialQueryError,
		reverseTrialError: experimentReverseTrialQueryError,
		loading: experimentPremiumOnboardModalLoading,
		trialDaysLeft,
	} = useIsPremiumOnboardingFeatureDiscoveryEnabled({
		isPremiumOnboardingEnabled: fg('cc_onboarding_experience'),
	});

	const shouldShowPremiumTrialDigestDataModal =
		trialDaysLeft <= 5 && trialDaysLeft > 0 && isSiteAdmin && edition === ConfluenceEdition.PREMIUM;

	const {
		showPTORTModal,
		showMidTrialModal: showPTORTMidTrialModal,
		loading: checkPTOModalVisibilityLoading,
	} = usePTOModalVisibilityOnReverseTrial({
		isPTORTEnabled,
		isSiteAdmin,
	});

	const { shouldShowMidTrialPremiumOnboardingModal, stopMidTrialPremiumOnboardingModal } =
		useShowMidTrialPremiumOnboardingModal({
			isPremiumOnboardingFeatureDiscoveryEnabled,
			isSiteAdmin,
			isTrialHalfWayThrough: isPTORTEnabled
				? showPTORTMidTrialModal
				: getIsTrialInLast15Days(trialDaysLeft),
		});

	if (
		edition !== ConfluenceEdition.PREMIUM ||
		isNewUser ||
		error ||
		loading ||
		experimentPremiumOnboardModalLoading ||
		checkPTOModalVisibilityLoading ||
		!fg('cc_onboarding_experience')
	)
		return null;

	// We dont want to visually display anything when experiment goes wrong.
	if (experimentInTrialQueryError) {
		return <ErrorDisplay error={experimentInTrialQueryError} />;
	}
	if (experimentReverseTrialQueryError) {
		return <ErrorDisplay error={experimentReverseTrialQueryError} />;
	}

	if (isPTORTEnabled && !showPTORTModal && !shouldShowMidTrialPremiumOnboardingModal) {
		return null;
	}

	//render premium trial digest data modal when iPUF eligibility conditions are met
	if (shouldShowPremiumTrialDigestDataModal) {
		return <PremiumTrialDigestDataModal daysLeft={trialDaysLeft} />;
	}

	// PremiumTrialOnboardingReverseTrialModal maybe returned before shouldShowMidTrialPremiumOnboardingModal becomes true.
	// This means that EP message PREMIUM_ONBOARDING_MESSAGE_ID will start before MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID.
	// However, PREMIUM_ONBOARDING_MESSAGE_ID start will fail because it should have already been exposed before mid trial modal shows.
	// Therefore, the mid trial modal is should be safe to start.
	if (shouldShowMidTrialPremiumOnboardingModal) {
		const source =
			isPTORTEnabled && showPTORTMidTrialModal
				? 'premiumTrialOnboardingReverseTrialMidtrialModal'
				: 'premiumTrialOnboardingModal';
		return (
			<CoordinationWrapper messageId={MID_TRIAL_PREMIUM_ONBOARDING_MESSAGE_ID}>
				{({ stop }) => {
					return (
						<PremiumTrialOnboardingModal
							daysLeft={trialDaysLeft}
							triggerFrom={TriggerFrom.MID_TRIAL_ONBOARDING}
							onClose={() => {
								stopMidTrialPremiumOnboardingModal();
								stop();
							}}
							source={source}
						/>
					);
				}}
			</CoordinationWrapper>
		);
	}

	if (isPremiumOnboardingFeatureDiscoveryEnabled && getIsPremiumOnboardingHappened(true))
		setIsPremiumOnboardingHappened(false);

	if (showPTORTModal) {
		return (
			<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
				{({ stop }) => {
					return <PremiumTrialOnboardingReverseTrialModal stop={stop} />;
				}}
			</CoordinationWrapper>
		);
	}

	if (isPremiumOnboardingFeatureDiscoveryEnabled && !isPTORTEnabled) {
		return (
			<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
				{({ stop }) => {
					return <PremiumTrialOnboardingModal triggerFrom={TriggerFrom.ONBOARDING} stop={stop} />;
				}}
			</CoordinationWrapper>
		);
	}

	return (
		<AdminKeyEligibilityQuery>
			{({ canBeSuperAdmin }) => {
				return (
					<CoordinationWrapper messageId={PREMIUM_ONBOARDING_MESSAGE_ID}>
						{({ stop }) => {
							return canBeSuperAdmin ? (
								<PremiumAdminOnboarding stop={stop} />
							) : (
								<PremiumUserOnboarding stop={stop} />
							);
						}}
					</CoordinationWrapper>
				);
			}}
		</AdminKeyEligibilityQuery>
	);
};
